'use client';

import classNames from '@sindresorhus/class-names';
import {type AppFromBackapi} from '@utils/fetchAppsByPackageNames';
import Image from 'next/image';
import {useEffect, useState} from 'react';
import type {Swiper as ISwiper} from 'swiper';
import 'swiper/css';
import {Navigation} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';

import chevron from '@web-landing/assets/images/ChevronRedesign.svg';

import {log} from '@web-landing/components/SearchPicks/MiniPicks/analytics';

import {
  DEFAULT_SWIPER_INSTANCE_WIDTH,
  SHORT_SPACE_12,
} from '@web-landing/constants/swiper';
import {miniPicksTestIds} from '@web-landing/constants/test-ids/mainLandingTestIds';

import {usePicksAnalytics} from '@web-landing/analytics/hooks/usePicksAnalytics';

import styles from './MiniPicks.module.scss';
import Pick from './Pick/Pick';

export default function SearchPicksRedesign({
  apps,
  metricsName,
}: {
  apps: Array<AppFromBackapi>;
  metricsName: 'GamesMonth' | 'MiniPicks';
}) {
  const [isPicksVisible, setIsPicksVisible] = useState(false);

  const [landingRecommendedAppsViewAnalytics, appSelectAnalytics] =
    usePicksAnalytics(isPicksVisible, setIsPicksVisible, metricsName);

  useEffect(() => {
    if (isPicksVisible) {
      landingRecommendedAppsViewAnalytics();
    }
  }, [isPicksVisible, landingRecommendedAppsViewAnalytics]);

  const handlePickAnalytics = (app: AppFromBackapi, index: number) => {
    appSelectAnalytics(app.appId, app.appName, index + 1, app.packageName);
    log.omicronFromLandingToVitrina();
  };

  const [swiperInstance, setSwiperInstance] = useState<ISwiper>();

  const [swiperState, setSwiperState] = useState<{
    isBeginning: boolean;
    isEnd: boolean;
  }>();

  return (
    <div className={styles.wrapper} id="picks">
      <div className={styles.swiperRelative}>
        <button
          aria-label="Назад"
          data-testid={miniPicksTestIds.miniPicksBackButton}
          onClick={() => swiperInstance?.slidePrev()}
          className={classNames(styles.arrow, styles.arrow__left, {
            [styles.hide__arrow]: swiperState?.isBeginning,
          })}
        >
          <Image
            src={chevron.src}
            alt=""
            aria-hidden={true}
            width={32}
            height={32}
          />
        </button>
        <Swiper
          spaceBetween={
            swiperInstance &&
            swiperInstance?.width < DEFAULT_SWIPER_INSTANCE_WIDTH
              ? SHORT_SPACE_12
              : 0
          }
          onSwiper={(swiper) => setSwiperInstance(swiper)}
          modules={[Navigation]}
          onProgress={(e) =>
            setSwiperState({isBeginning: e.isBeginning, isEnd: e.isEnd})
          }
          slidesPerView="auto"
        >
          {apps.map((pick, index) => (
            <SwiperSlide key={index} className={styles.pick}>
              <a
                href={pick.catalogUrl}
                onClick={() => handlePickAnalytics(pick, index)}
                data-testid={
                  metricsName === 'GamesMonth'
                    ? miniPicksTestIds.gamesMiniPick
                    : miniPicksTestIds.miniPick
                }
              >
                <Pick pick={pick} />
              </a>
            </SwiperSlide>
          ))}
        </Swiper>
        <button
          aria-label="Вперед"
          data-testid={miniPicksTestIds.miniPicksNextButton}
          onClick={() => swiperInstance?.slideNext()}
          className={classNames(styles.arrow, {
            [styles.hide__arrow]: swiperState?.isEnd,
          })}
        >
          <Image
            aria-hidden={true}
            src={chevron.src}
            width={32}
            height={32}
            alt=""
          />
        </button>
      </div>
    </div>
  );
}
